import React, { FC, ReactNode } from 'react';
import cn from 'classnames';
import { CandyRackSettings } from '../../types';
import { usePopupContext } from '../../contexts/Settings';

type Props = {
  className?: string;
  children: ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
} & React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

const AddToCartButton: FC<Props> = ({
  children,
  className,
  disabled,
  ...rest
}) => {
  const {
    settings: {
      buttons: { add },
    },
    blockingActionInProgress,
  } = usePopupContext();
  const isDisabled = disabled || blockingActionInProgress;

  return (
    <button
      disabled={isDisabled}
      {...rest}
      className={cn(
        `text-readableAddButtonTextColor
          flex items-center justify-center font-semibold
          py-8 px-20 w-full text-md uppercase bg-addButtonBackgroundColor-1`,
        isDisabled || blockingActionInProgress
          ? 'bg-addButtonBackgroundColor-3 cursor-default'
          : 'cursor-pointer transition-colors duration-100 hover:bg-addButtonBackgroundColor-2',
        add.border.enabled &&
          'border-addButtonBorderWidth border-addButtonBorderColor-1 hover:border-addButtonBorderColor-2',
        add.border.enabled && isDisabled && 'border-addButtonBorderColor-3',
        className,
      )}
      style={{ overflowWrap: 'anywhere', wordBreak: 'normal' }}
    >
      {children}
    </button>
  );
};

const UpgradeButton: FC<Props> = ({
  children,
  className,
  disabled,
  ...rest
}) => {
  const {
    settings: {
      buttons: { add },
    },
    blockingActionInProgress,
  } = usePopupContext();
  const isDisabled = disabled || blockingActionInProgress;

  return (
    <button
      disabled={isDisabled}
      {...rest}
      className={cn(
        `text-readableAddButtonTextColor
        flex items-center justify-center font-semibold
        py-8 px-20 w-full text-md uppercase bg-addButtonBackgroundColor-1`,
        isDisabled || blockingActionInProgress
          ? 'bg-addButtonBackgroundColor-3 cursor-default'
          : 'cursor-pointer transition-colors duration-100 hover:bg-addButtonBackgroundColor-2',
        add.border.enabled &&
          'border-addButtonBorderWidth border-addButtonBorderColor-1 hover:border-addButtonBorderColor-2',
        add.border.enabled && isDisabled && 'border-addButtonBorderColor-3',
        className,
      )}
      style={{ overflowWrap: 'anywhere', wordBreak: 'normal' }}
    >
      {children}
    </button>
  );
};

const ContinueToButton: FC<Props> = ({
  children,
  className,
  isLoading,
  disabled,
  ...rest
}) => {
  const {
    settings: {
      buttons: { continue_to },
    },
    blockingActionInProgress,
  } = usePopupContext();
  const isDisabled = disabled || blockingActionInProgress;

  return (
    <button
      {...rest}
      className={cn(
        `text-readableContinueToButtonTextColor
        flex items-center justify-center font-semibold
        py-8 px-20 w-full md:w-auto  text-md uppercase bg-continueToButtonBackgroundColor-1`,
        isDisabled
          ? 'bg-continueToButtonBackgroundColor-3 cursor-default'
          : 'cursor-pointer transition-colors duration-100 hover:bg-continueToButtonBackgroundColor-2',
        continue_to.border.enabled &&
          'border-continueToButtonBorderWidth border-continueToButtonBorderColor-1 hover:border-continueToButtonBorderColor-2',
        continue_to.border.enabled &&
          isDisabled &&
          'border-continueToButtonBorderColor-3',
        isLoading && 'button--loading',
        className,
      )}
    >
      {children}
    </button>
  );
};

// Compatible with CR settings only
const RejectButton: FC<Props> = ({
  children,
  className,
  isLoading,
  disabled,
  ...rest
}) => {
  const context = usePopupContext();
  const {
    buttons: { reject },
  } = context.settings as CandyRackSettings;
  const isDisabled = disabled || context.blockingActionInProgress;

  if (!reject) {
    console.assert(!!reject, 'Reject Button used outside CR settings scope');
  }

  return (
    <button
      {...rest}
      className={cn(
        `text-readableRejectButtonTextColor
        flex items-center justify-center font-semibold
        py-8 px-20 w-full md:w-auto  text-md uppercase bg-rejectButtonBackgroundColor-1`,
        isDisabled
          ? 'bg-rejectButtonBackgroundColor-3 cursor-default'
          : 'cursor-pointer transition-colors duration-100 hover:bg-rejectButtonBackgroundColor-2',
        reject.border.enabled &&
          'border-rejectButtonBorderWidth border-rejectButtonBorderColor-1 hover:border-rejectButtonBorderColor-2',
        reject.border.enabled &&
          isDisabled &&
          'border-rejectButtonBorderColor-3',
        isLoading && 'button--loading',
        className,
      )}
    >
      {children}
    </button>
  );
};

export { ContinueToButton, AddToCartButton, UpgradeButton, RejectButton };

export default ContinueToButton;
